import React, {useEffect, useState} from 'react';
import {withTranslation} from 'react-i18next';
import {Button, Card} from '@betareader/design-system';
import Avatar from '../avatar/avatar';
import SocialProfileLinks from '../../containers/readers/components/socialProfileLinks';
import UserReviewsModal from './userReviewsModal';

export const UserBadge = ({t, user, showInviteButton = true, locked = false, onInvite}) => {
  const [showAllGenres, setShowAllGenres] = useState(false);
  const [showReviewsModal, setShowReviewsModal] = useState(false);
  const [isNarrow, setIsNarrow] = useState(() => window.innerWidth <= 450);

  useEffect(() => {
    const updateWidth = () => setIsNarrow(window.innerWidth <= 450);
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  if (!user) return null;

  const blurStyle = locked
    ? {filter: 'blur(5px)', userSelect: 'none', pointerEvents: 'none'}
    : undefined;
  const genres = user.preferredGenres || [];
  const visibleLimit = isNarrow ? 1 : 3;
  const visibleGenres = showAllGenres ? genres : genres.slice(0, visibleLimit);

  return (
    <>
      <Card className="br-user-badge">
        <div className="br-user-badge__photo" style={blurStyle} aria-hidden={locked}>
          <Avatar name={user.displayName} userId={locked ? undefined : user._id}
            src={user.photoUrl} diameter={50} />
        </div>
        <div className="br-user-badge__body">
          <div className="br-user-badge__heading">
            <strong style={blurStyle} aria-hidden={locked}>
              <Avatar name={user.displayName} userId={locked ? undefined : user._id} nameOnly />
            </strong>
            {user.averageRating > 0 && (
              <span className="br-user-badge__rating">
                <span role="img" aria-label={`${t('OverallRating', 'Rating')}: ${user.averageRating} out of 5`}>
                  ★ {user.averageRating}/5
                </span>
                <Button variant="ghost" size="small" onClick={() => setShowReviewsModal(true)}
                  aria-label={t('ClickToSeeAllReviews', 'See all reviews')}>
                  ({user.reviewCount || 0})
                </Button>
              </span>
            )}
            {user.socialProfiles && (
              <span className="br-user-badge__social" style={blurStyle} aria-hidden={locked}>
                <SocialProfileLinks socialProfiles={user.socialProfiles} disabled={locked} />
              </span>
            )}
          </div>
          {user.preferredLanguages?.length > 0 && (
            <div className="br-user-badge__chips" aria-label={t('Languages', 'Languages')}>
              {user.preferredLanguages.map(language => {
                const name = language.nativeName || language;
                return <span className="br-user-badge__chip" key={name}>{name}</span>;
              })}
            </div>
          )}
          {genres.length > 0 && (
            <div className="br-user-badge__chips" aria-label={t('Genres', 'Genres')}>
              {visibleGenres.map(genre => <span className="br-user-badge__chip" key={genre}>{genre}</span>)}
              {genres.length > visibleLimit && (
                <Button variant="ghost" size="small" aria-expanded={showAllGenres}
                  onClick={() => setShowAllGenres(value => !value)}>
                  {showAllGenres ? t('ShowFewerGenres', 'Show fewer genres') : t('ShowAllGenres', 'Show all genres')}
                </Button>
              )}
            </div>
          )}
          {user.bio && <p className="br-user-badge__bio">{user.bio}</p>}
        </div>
        {showInviteButton && (
          <Button className="br-user-badge__invite" onClick={onInvite}>
            {locked ? t('UpgradeToInvite', 'Upgrade to invite') : t('Invite')}
          </Button>
        )}
      </Card>
      <UserReviewsModal open={showReviewsModal} onClose={() => setShowReviewsModal(false)} user={user} />
    </>
  );
};

export default withTranslation()(UserBadge);
